import { Alert, Card, Collapse, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { FlowDiagram, FlowLegend, FlowStep } from './FlowDiagram';

const { Title, Paragraph, Text } = Typography;

interface IProps {
  title?: string;
}

// ── 検索系エンジン共通フロー ─────────────────────────────
const COMMON_STEPS: FlowStep[] = [
  {
    kind: 'local',
    title: '対象メンバー解決',
    sub: '住所（郵便番号）が設定されているメンバーの中で最年長を親として選ぶ。admin から明示指定も可',
  },
  {
    kind: 'local',
    title: 'プロンプト組み立て',
    sub: 'エンジン本文テンプレート + フクマル人格（共通ルール）+ フッターを展開',
    promptKeys: ['common.system', 'common.rules', 'common.footer'],
  },
  {
    kind: 'search',
    title: 'Tavily 事前検索',
    sub: 'エンジン別のクエリ（*.searchQueries で管理・1行=1クエリ）を並列検索 → マージして検索結果ブロックを注入',
  },
  {
    kind: 'llm',
    title: '本文生成',
    sub: 'claude-sonnet-4-6（web_searchツールなし）・<final>抽出・選定ログ分離。TAVILY_API_KEY 未設定時は Anthropic web_search にフォールバック',
  },
  {
    kind: 'db',
    title: 'agent_messages に保存',
    sub: 'generationLog に検索クエリ・参照URL・使用プロンプト版（promptVersions）を記録',
  },
  {
    kind: 'deliver',
    title: '家族トーク投稿 + push 通知',
    sub: 'post=true 時のみ。フクマル（Bot）として投稿 → socket 即時反映 → 家族全員へ push',
    optional: true,
  },
];

// ── エンジン別（検索部分の詳細） ─────────────────────────
const ENGINE_SECTIONS: {
  key: string;
  label: string;
  steps: FlowStep[];
  note?: string;
}[] = [
  {
    key: 'outing',
    label: 'おでかけ理由（outing）— 生活圏を広げて「今日行く理由」を探す',
    steps: [
      {
        kind: 'llm',
        title: '生活圏の地名展開',
        sub: 'claude-sonnet-4-6 ・ 住所から車30-40分/電車60分圏の市区町村を方角バランスよく8〜10個展開',
        promptKeys: ['outing.expandNeighbors'],
      },
      {
        kind: 'search',
        title: 'Tavily 5クエリ（近隣3群に分散）',
        sub: '地元当日 / 近隣A / 近隣B / 近隣C / 県内フォールバック ・ 各6件',
        promptKeys: ['outing.searchQueries'],
      },
      {
        kind: 'local',
        title: 'ラウンドロビン・マージ（最大15件）',
        sub: '各クエリから1件ずつ交互に採用 → 強いエリアが枠を独占しない',
      },
      {
        kind: 'local',
        title: '検索結果ブロック注入',
        sub: '信頼性ルール・生活圏・選定ログ指示を付けて本文プロンプトに連結',
        promptKeys: ['outing.main', 'outing.searchSuffix'],
      },
      {
        kind: 'llm',
        title: '本文生成（max_tokens 4000）',
        sub: '①強イベント枠 ②近場/電車枠 ③補助枠 ・ 選定ログは本文から分離して generationLog へ',
      },
    ],
    note: '「──[選定ログ]──」以降は投稿本文から自動で取り除かれ、生成一覧の生成ログで確認できます。バッチ実行時は実行日ではなく「次の土曜日」を対象日として生成します（手動実行は任意日付）。',
  },
  {
    key: 'kinjo',
    label: '今週の近所穴場（kinjo）— 徒歩・自転車圏の小さな催し',
    steps: [
      {
        kind: 'local',
        title: '週末・探索期間の計算',
        sub: '今日から次の日曜まで（最大7日間）。平日のマルシェ・朝市も対象',
      },
      {
        kind: 'search',
        title: 'Tavily 5クエリ（季節フック入り）',
        sub: 'マルシェ直売 / 月別の季節フック / 古民家カフェ体験 / 地域コミュニティ / 市域公式 ・ 各10件',
        promptKeys: ['kinjo.searchQueries'],
      },
      {
        kind: 'local',
        title: 'ラウンドロビン・マージ（最大15件）+ 注入',
        sub: '距離ルール（徒歩15分・車10-15分）と穴場定義を付けて連結',
        promptKeys: ['kinjo.main', 'kinjo.searchSuffix'],
      },
      {
        kind: 'llm',
        title: '本文生成（max_tokens 3000）',
        sub: '質を担保できる件数だけ出す（無理に3件埋めない）',
      },
    ],
  },
  {
    key: 'otoku',
    label: '今やると得（otoku）— 特売・ポイントデー・キャンペーン',
    steps: [
      {
        kind: 'search',
        title: 'Tavily 3クエリ',
        sub: '今日の特売チラシ / 曜日ポイントデー / 今月のキャンペーン ・ 各6件 → 連結10件',
        promptKeys: ['otoku.searchQueries'],
      },
      {
        kind: 'local',
        title: '検索結果ブロック注入',
        sub: '曜日・日付連動の定例得（イオン感謝デー等）は本文テンプレートの知識ベース側',
        promptKeys: ['otoku.main', 'otoku.searchSuffix'],
      },
      {
        kind: 'llm',
        title: '本文生成（max_tokens 3000）',
        sub: '①強得枠 ②実行しやすいもの ③次の得タイミング ④行動ヒント',
      },
    ],
  },
  {
    key: 'kigen',
    label: '期限チェック（kigen）— 締切カウントダウン',
    steps: [
      {
        kind: 'search',
        title: 'Tavily 4クエリ（損失カテゴリ別）',
        sub: '給付金・助成金 / 税金（都道府県）/ 健診・予防接種 / その他締切 ・ 各6件 → 連結12件',
        promptKeys: ['kigen.searchQueries'],
      },
      {
        kind: 'local',
        title: '検索結果ブロック注入',
        sub: '多様性ルール（①②③は異なるカテゴリから）を付けて連結',
        promptKeys: ['kigen.main', 'kigen.searchSuffix'],
      },
      {
        kind: 'llm',
        title: '本文生成（max_tokens 3500）',
        sub: '緊迫度 🔴7日以内 / 🟠14日 / 🟡30日 / 🔵31日以上 ・「あと〇日」を全件に明記',
      },
    ],
  },
  {
    key: 'worry',
    label: '心配ごと（worry）— 親の地域の天気・健康リスク',
    steps: [
      {
        kind: 'local',
        title: '気象の節目データ取得',
        sub: '気象庁スクレイピングの日次キャッシュ（agent_weather_snapshots）から梅雨・台風・初夏日等を取得しプロンプトに埋め込む',
      },
      {
        kind: 'search',
        title: 'Tavily 3クエリ',
        sub: '感染症（県）/ 熱中症・花粉（市）/ 台風（全国・5件） ・ 連結8件。今日の気温は検索結果（tenki.jp等）から',
        promptKeys: ['worry.searchQueries'],
      },
      {
        kind: 'local',
        title: '検索結果ブロック注入',
        sub: '「今日の数字を必ず入れる・一般論禁止」ルールを付けて連結',
        promptKeys: ['worry.main', 'worry.searchSuffix'],
      },
      {
        kind: 'llm',
        title: '本文生成（max_tokens 2500）',
        sub: '①今日の天気・気温 ②今週の健康リスク ③地域情報',
      },
    ],
  },
  {
    key: 'shocho',
    label: '今日の便り（shocho）— 誕生日・記念日・季節の節目（検索なし）',
    steps: [
      {
        kind: 'local',
        title: 'ファクト収集（家族全体）',
        sub: '全メンバーの誕生日（節目・うるう年補正）/ 登録記念日 / 暦の節目 / 気象トリガー（昨日のみ判定・他地域比較）',
      },
      {
        kind: 'local',
        title: 'priority 順に整形',
        sub: '誕生日10 > 台風・記念日9 > 梅雨8 > 桜7 > 季節節目6 > 暦5 ・「（必ず祝う）」マーカー付与',
      },
      {
        kind: 'local',
        title: 'ファクトなしの日はスキップ',
        sub: '発火しない（沈黙は放置）。ジョブは正常完了し「発火なし」と表示される',
      },
      {
        kind: 'llm',
        title: '便り生成（max_tokens 1000・検索なし）',
        sub: '複数ファクトを流れのある1投稿に統合。フッター（📎/⚠️）は付けない',
        promptKeys: ['shocho.main'],
      },
    ],
    note: '対象は家族全体（対象メンバーの指定は使いません）。地域は全メンバーの住所から都道府県単位で集約されます。',
  },
];

export const AgentFlow: React.FC<IProps> = ({ title }) => {
  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            AIエージェント 生成フロー解説
          </Title>
          <Paragraph type='secondary' style={{ marginBottom: 8 }}>
            フクマルの各エンジンが「どのプロンプトで・いつ LLM / Web検索が走るか」の解説です。
            青字のプロンプト key をクリックすると、その履歴・編集画面が開きます。
          </Paragraph>
          <FlowLegend />
        </header>

        <Alert
          type='info'
          showIcon
          message='実行は手動実行ページ・日次バッチのどちらからでも同じフローです。日次バッチは「アクティブ家族（2名以上・メンバーの最終ログインが1週間以内）」を自動選定し、検索系5エンジンの日替わりローテーション（outing→kinjo→otoku→kigen→worry）を毎朝6:00、shocho を毎朝6:30 に実行します'
        />

        <Card title='共通フロー（検索系5エンジン: outing / kinjo / otoku / kigen / worry）'>
          <FlowDiagram steps={COMMON_STEPS} ariaLabel='エージェント共通フロー' />
          <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
            モデルは全エンジン <Text code>claude-sonnet-4-6</Text>
            。Tavily で事前検索してプロンプトに埋め込むため、生成時の web_search
            ツールは使いません（コスト削減）。使用したプロンプトの version
            は生成ログの promptVersions に記録されます。
          </Paragraph>
        </Card>

        <Card title='エンジン別の詳細（検索・整形部分）'>
          <Collapse
            items={ENGINE_SECTIONS.map((e) => ({
              key: e.key,
              label: e.label,
              children: (
                <div>
                  <FlowDiagram steps={e.steps} ariaLabel={`${e.key} のフロー`} />
                  {e.note && (
                    <Paragraph
                      type='secondary'
                      style={{ marginTop: 8, marginBottom: 0 }}
                    >
                      ※ {e.note}
                    </Paragraph>
                  )}
                </div>
              ),
            }))}
          />
        </Card>

        <Card title='コスト目安（1回あたり・設計時の概算）'>
          <ul style={{ marginBottom: 0 }}>
            <li>outing … 約 $0.06〜0.074（Tavily 5クエリ + 生活圏展開 Sonnet + 本文生成）</li>
            <li>kinjo … 約 $0.06（Tavily 5クエリ + 本文生成）</li>
            <li>otoku … 約 $0.04（Tavily 3クエリ + 本文生成）</li>
            <li>kigen … 約 $0.06（Tavily 4クエリ + 本文生成）</li>
            <li>worry … 約 $0.05（Tavily 3クエリ + 気象キャッシュ + 本文生成）</li>
            <li>shocho … 最小（検索なし・max_tokens 1000。気象は都道府県単位の日次キャッシュを共用）</li>
          </ul>
        </Card>
      </div>
    </div>
  );
};
